import { useMemo, useState } from "react";
import { ChevronRight, Plus, Search } from "lucide-react";
import { Button, Card, CardEmpty, CardEmptyAction, Icon, Rows, Spinner } from "@cntrl/ui";
import { useBandContext, useCapability, useIsPro } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useBandTeam, useBandSeatInvites, type BandSeatView } from "../../app/hooks/useBandTeam.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { CrewSeatRow, PendingInviteRow, InviteCrewModal, CrewError } from "./CrewSeatRow.js";
import { BandProfileBrowser } from "./BandProfileModal.js";
import { CrewProfileModal } from "./CrewProfileModal.js";
import { RoleLabel } from "./RoleLabel.js";
import { CREW_PARTIES } from "./crewParty.js";
import styles from "./YourCrewCard.module.scss";

/**
 * "Your Crew" — the spec's first card, and the ONE list of people on this band: active seats plus
 * outstanding invites. The contacts book is GONE (PO, 2026-08-20): "a contact" was a parallel
 * off-platform person mechanism nobody could explain, so everyone who matters is crew — a seat, or a
 * pending invite carrying the name that becomes their account. The All/Crew category chips went with
 * the book (there is only crew), as did Add Contact.
 *
 * The OWNER seat never renders as a crew ROW (PO, 2026-08-20 — the Artist IS the band): the band
 * itself heads the card as a pseudo-row (main photo · name · "Artist") that opens the BAND profile,
 * and the crew rows beneath it are the humans. A band cannot be its own crew member.
 *
 * The seat list is `band:read` since the 752 widening — every member sees the whole crew. The INVITE
 * list and the Add Crew door stay `band:manage`.
 */
// The owner seat's role key (DB-pinned: is_owner ⇒ band_member_av). A const so jsx-a11y doesn't read
// RoleLabel's `role` prop as an ARIA role literal.
const OWNER_SEAT_ROLE = "band_member_av";

export function YourCrewCard({ onOpenPerson }: { onOpenPerson?: (personId: string) => void } = {}) {
  const canManage = useCapability("band:manage");
  // ART-144: Pro groups this card by touring party. Standard's flat list is untouched.
  const isPro = useIsPro();
  const { bandId } = useBandContext();

  const team = useBandTeam();
  // GET /team/invites is band:manage-only — don't fire it for a seat that would just 403.
  const invites = useBandSeatInvites(canManage);
  const me = useMe();

  const [search, setSearch] = useState("");
  const [inviting, setInviting] = useState(false);
  // The crew member whose profile is open. A PERSON id, not the seat object, so the modal always renders
  // the LIVE row — an optimistic role change repaints it instead of showing a stale snapshot — and so this
  // door opens the very same dialog the band profile's member rows do.
  const [viewing, setViewing] = useState<string | null>(null);
  // The band-header pseudo-row opens the BAND profile — the Artist IS the band (PO, 2026-08-20).
  const [viewingBand, setViewingBand] = useState(false);

  const myPersonId = me.data?.person?.personId;
  // The owner seat is the BAND (the header), never a crew row.
  const seats = useMemo(
    () => (team.data?.items ?? []).filter((s) => s.seatId && !s.isOwner),
    [team.data],
  );
  const pending = useMemo(() => invites.data?.items ?? [], [invites.data]);

  // The band identity for the header — the me-document's seat carries the band's name and MAIN photo
  // (the same pair the shell's band switcher renders).
  const bandSeat = me.data?.seats?.find((s) => s.kind === "band" && s.bandId === bandId);
  // The band's identity picture via the global avatar route — one band-photo path across the app.
  const bandPhoto = useAvatarUrl("band", bandId, bandSeat?.avatarMediaId);

  const q = search.trim().toLowerCase();
  const shownSeats = seats.filter(
    (s) => !q || [s.displayName, s.email].filter(Boolean).join(" ").toLowerCase().includes(q),
  );
  // Pending invites show the payload names (the account-creation seed) — never a bare mailbox.
  const shownPending = pending.filter(
    (i) =>
      !q ||
      [i.firstName, i.lastName, i.email].filter(Boolean).join(" ").toLowerCase().includes(q),
  );

  // ONE row renderer for both tiers, so the grouped and flat lists cannot drift into rendering a crew
  // member two different ways.
  const crewRow = (seat: BandSeatView) => (
    <CrewSeatRow
      key={seat.seatId}
      seat={seat}
      isSelf={!!myPersonId && seat.personId === myPersonId}
      // Desktop opens the person modal; mobile navigates to the dedicated person screen (ART-194) via the
      // callback the route wrapper supplies. No callback ⇒ modal, so this card stays render-context free.
      onOpen={() =>
        seat.isOwner
          ? setViewingBand(true)
          : onOpenPerson && seat.personId
            ? onOpenPerson(seat.personId)
            : setViewing(seat.personId ?? null)
      }
    />
  );

  const totalPeople = seats.length + pending.length;
  const loading = team.isLoading;
  const shownCount = shownSeats.length + shownPending.length;

  return (
    <Card
      id="crew"
      title="Crew"
      flush
      // Title-only now (its subtitle went, 2026-10-08): float the action like Friends does, or the 36px button
      // stretches the header past the "Crew" line and sits the title lower than its neighbours'.
      floatActions
      actions={
        canManage && (
          <Button size="sm" onClick={() => setInviting(true)}>
            <Icon icon={Plus} size={16} /> Add crew
          </Button>
        )
      }
    >
      {/* The band heads its own crew card (PO, 2026-08-20): a pseudo-row — main photo · name ·
          "Artist" — that opens the BAND profile. Not a seat row: the Artist IS the band. */}
      {!loading && !team.isError && bandSeat && (
        <button type="button" className={styles.bandHead} onClick={() => setViewingBand(true)}>
          {bandPhoto ? (
            <img className={styles.bandHeadPhoto} src={bandPhoto} alt="" data-photo="" />
          ) : (
            <span className={styles.bandHeadPhoto} aria-hidden="true">
              {orgInitials(bandSeat.displayName ?? "")}
            </span>
          )}
          <span className={styles.bandHeadName}>{bandSeat.displayName}</span>
          <RoleLabel role={OWNER_SEAT_ROLE} owner className={styles.bandHeadRole} />
          <Icon icon={ChevronRight} size={20} className={styles.bandHeadChevron} />
        </button>
      )}

      {/* Search appears only once there is somebody to search for. */}
      {!loading && !team.isError && totalPeople > 0 && (
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Icon icon={Search} size={16} className={styles.searchIcon} />
            <input
              type="search"
              className={styles.search}
              placeholder="Search crew…"
              aria-label="Search crew"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className={styles.body}>
        {loading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading crew…" />
          </div>
        ) : team.isError ? (
          <CrewError error={team.error} onRetry={() => void team.refetch()} />
        ) : totalPeople === 0 ? (
          <CardEmpty>
            {canManage ? (
              <>
                No crew yet, click <CardEmptyAction>Add crew</CardEmptyAction> to start your team.
              </>
            ) : (
              <>No crew yet.</>
            )}
          </CardEmpty>
        ) : shownCount === 0 ? (
          <CardEmpty>No matches.</CardEmpty>
        ) : (
          /* Standard: one flat list — seats, then anyone invited who hasn't accepted.
             PRO: the same list GROUPED BY PARTY (ART-144 §5.1's first function — "instead of your crew it
             should show which team you are in"), in the fixed A/B/C/Production/Drivers order, with only
             NON-EMPTY parties drawing a heading.

             The pending invites stay UNGROUPED at the end on both tiers, and that is a fact rather than a
             simplification: an invite has no seat, so it has no party — the title it was issued for implies
             one, but the person is not on the run yet and placing them would be the client guessing at a
             resolution the server owns. */
          <>
            {isPro ? (
              CREW_PARTIES.map(({ key, label }) => {
                const members = shownSeats.filter((s) => s.party === key);
                if (members.length === 0) return null;
                return (
                  // role="group" + the party's name, so the heading is not merely a visual one: a screen
                  // reader navigating a grouped crew list is told which part of the run each block is.
                  <div key={key} role="group" aria-label={label}>
                    <p className={styles.partyGroup}>{label}</p>
                    <Rows>{members.map(crewRow)}</Rows>
                  </div>
                );
              })
            ) : (
              <Rows>{shownSeats.map(crewRow)}</Rows>
            )}
            {shownPending.length > 0 && (
              <Rows>
                {shownPending.map((inv) => (
                  <PendingInviteRow key={inv.inviteId} invite={inv} />
                ))}
              </Rows>
            )}
          </>
        )}
      </div>

      {viewing && <CrewProfileModal personId={viewing} onClose={() => setViewing(null)} />}
      {viewingBand && <BandProfileBrowser initialBandId={bandId} onClose={() => setViewingBand(false)} />}
      {inviting && <InviteCrewModal onClose={() => setInviting(false)} />}
    </Card>
  );
}
